/*
 * Obermaier — search.js
 * <om-predictive-search>: the search overlay (combobox + listbox, debounced predictive search,
 *   recent searches on this device, loading / empty / error states).
 * <om-search-results>: the results page (facets in a sheet, sort, active filters, live updates
 *   through the Section Rendering API, history support).
 * Both keep Shopify's server-side availability filter (options[unavailable_products]=hide) on every
 * request so counts and pages stay correct.
 */
import { config, debounce, define, on, openSheet, closeSheet, announce, initReveal, preferencesAllowed } from '@theme/core';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 180;
const RECENT_KEY = 'om:recent-searches';
const RECENT_MAX = 6;
const AVAILABILITY = { 'options[prefix]': 'last', 'options[unavailable_products]': 'hide' };
const coarse = matchMedia('(pointer: coarse)');

/* ------------------------------------------------------------ helpers */
function searchUrl(term, base = config.routes.search || '/search') {
  const url = new URL(base, location.origin);
  url.searchParams.set('q', term);
  for (const [k, v] of Object.entries(AVAILABILITY)) url.searchParams.set(k, v);
  return url.pathname + url.search;
}

// Recent searches live only in this browser. Every access is guarded: storage may be blocked.
// They are a convenience, so they are only stored once the visitor allowed preferences in the cookie
// banner (§ 25 TDDDG, preferencesAllowed in core.js).

const recent = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string').slice(0, RECENT_MAX) : [];
    } catch {
      return [];
    }
  },
  async add(term) {
    const clean = term.trim().slice(0, 80);
    if (clean.length < MIN_CHARS || !(await preferencesAllowed())) return;
    const list = recent.read().filter((s) => s.toLowerCase() !== clean.toLowerCase());
    list.unshift(clean);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch {}
  },
  clear() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {}
  },
};

/* ------------------------------------------------- predictive overlay */
class OmPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.form = this.querySelector('[data-form]');
    this.input = this.querySelector('input[type="search"]');
    this.clearBtn = this.querySelector('[data-clear]');
    this.body = this.querySelector('[data-body]');
    this.results = this.querySelector('[data-results]');
    this.listbox = this.querySelector('[data-listbox]');
    this.message = this.querySelector('[data-message]');
    this.skeleton = this.querySelector('[data-skeleton]');
    this.idle = this.querySelector('[data-idle]');
    this.status = this.querySelector('[data-status]');
    this.recentBlock = this.querySelector('[data-recent]');
    this.recentList = this.querySelector('[data-recent-list]');
    try {
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    } catch {
      this.strings = {};
    }

    this.cache = new Map();
    this.controller = null;
    this.activeIndex = -1;
    this.lastTerm = '';
    this.runDebounced = debounce(() => this.search(), DEBOUNCE_MS);

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.input.addEventListener('blur', () => this.setActive(-1));
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.clearBtn?.addEventListener('click', () => this.clear({ focus: true }));
    this.addEventListener('click', (e) => this.onClick(e));

    // Hide the on-screen keyboard as soon as someone starts scrolling through results.
    this.body?.addEventListener('touchmove', () => {
      if (coarse.matches && document.activeElement === this.input) this.input.blur();
    }, { passive: true });

    if (this.dialog) {
      this.dialog.addEventListener('om:sheet:open', () => this.onOpen());
      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('cancel', (e) => {
        // Escape first clears the field, a second Escape closes the overlay.
        if (this.input.value) {
          e.preventDefault();
          this.clear({ focus: true });
        }
      });
    }

    this.renderRecent();
    on('om:preferences:revoked', () => this.renderRecent());
  }

  /* -------------------------------------------------------- lifecycle */
  onOpen() {
    this.renderRecent();
    requestAnimationFrame(() => {
      this.input.focus({ preventScroll: true });
      if (this.input.value) this.input.select();
    });
    if (this.term.length >= MIN_CHARS && this.listbox.hidden && this.message.hidden) this.search();
  }

  onClose() {
    this.controller?.abort();
    this.setLoading(false);
    this.setActive(-1);
  }

  get term() {
    return this.input.value.trim();
  }

  /* ------------------------------------------------------------ input */
  onInput() {
    const term = this.term;
    this.clearBtn.hidden = this.input.value.length === 0;
    if (term.length < MIN_CHARS) {
      this.controller?.abort();
      this.showIdle();
      return;
    }
    if (this.cache.has(term)) {
      this.render(this.cache.get(term), term);
      return;
    }
    this.setLoading(true);
    this.runDebounced();
  }

  clear({ focus = false } = {}) {
    this.input.value = '';
    this.clearBtn.hidden = true;
    this.controller?.abort();
    this.showIdle();
    if (focus) this.input.focus({ preventScroll: true });
  }

  onSubmit(event) {
    const term = this.term;
    if (!term) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    recent.add(term);
  }

  onKeydown(event) {
    const expanded = this.input.getAttribute('aria-expanded') === 'true';
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!expanded) return;
        event.preventDefault();
        const options = this.options;
        if (!options.length) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        let next = this.activeIndex + step;
        if (next >= options.length) next = 0;
        if (next < 0) next = options.length - 1;
        this.setActive(next);
        break;
      }
      case 'Enter': {
        const option = this.options[this.activeIndex];
        if (!option) return;
        event.preventDefault();
        recent.add(option.dataset.term || this.term);
        option.click();
        break;
      }
      case 'Escape': {
        if (this.activeIndex > -1) {
          event.preventDefault();
          this.setActive(-1);
        }
        break;
      }
      default:
        break;
    }
  }

  onClick(event) {
    const chip = event.target.closest('[data-search-term]');
    if (chip) {
      event.preventDefault();
      this.input.value = chip.dataset.searchTerm;
      this.onInput();
      if (!coarse.matches) this.input.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-recent-clear]')) {
      recent.clear();
      this.renderRecent();
      this.say(this.strings.recentCleared || '');
      this.input.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-retry]')) {
      this.cache.delete(this.term);
      this.search();
      return;
    }
    const option = event.target.closest('[role="option"]');
    if (option && this.listbox.contains(option)) recent.add(option.dataset.term || this.term);
  }

  /* ---------------------------------------------------------- network */
  async search() {
    const term = this.term;
    if (term.length < MIN_CHARS) return;
    if (this.cache.has(term)) {
      this.render(this.cache.get(term), term);
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);

    const url = new URL(this.dataset.url || config.routes.predictiveSearch || '/search/suggest', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,page,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('section_id', this.dataset.section || 'predictive-search');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      this.cache.set(term, html);
      if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
      if (term !== this.term) return;
      this.render(html, term);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (term === this.term) this.renderError(term);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setLoading(false);
      }
    }
  }

  /* ------------------------------------------------------------ views */
  render(html, term) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('[data-ps-root]');
    if (!root) {
      this.renderError(term);
      return;
    }
    const total = Number(root.dataset.total || 0);
    const list = root.querySelector('[data-ps-list]');
    const empty = root.querySelector('[data-ps-empty]');

    this.setLoading(false);
    this.setActive(-1);
    this.results.hidden = false;
    this.skeleton.hidden = true;
    this.lastTerm = term;

    if (total > 0 && list) {
      this.listbox.replaceChildren(...list.children);
      this.listbox.hidden = false;
      this.message.hidden = true;
      this.message.replaceChildren();
      this.idle.hidden = true;
      this.classList.remove('is-empty');
      this.input.setAttribute('aria-expanded', 'true');
    } else {
      this.listbox.replaceChildren();
      this.listbox.hidden = true;
      this.message.replaceChildren(...(empty ? [empty] : []));
      this.message.hidden = !empty;
      // Suggestions and quick links stay available below the "no results" note.
      this.idle.hidden = false;
      this.classList.add('is-empty');
      this.input.setAttribute('aria-expanded', 'false');
    }
    this.body.scrollTop = 0;
    this.say(root.dataset.summary || '');
  }

  renderError(term) {
    this.setLoading(false);
    this.setActive(-1);
    this.results.hidden = false;
    this.skeleton.hidden = true;
    this.listbox.hidden = true;
    this.listbox.replaceChildren();
    this.idle.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');

    const box = document.createElement('div');
    box.className = 'om-ps-empty om-ps-empty--error';
    const title = document.createElement('p');
    title.className = 'om-ps-empty__title om-h3';
    title.textContent = this.strings.errorTitle || '';
    const text = document.createElement('p');
    text.className = 'om-ps-empty__text';
    text.textContent = this.strings.errorText || '';
    const actions = document.createElement('div');
    actions.className = 'om-ps-empty__actions';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'om-btn om-btn--sm';
    retry.dataset.retry = '';
    retry.textContent = this.strings.retry || 'Retry';
    const full = document.createElement('a');
    full.className = 'om-btn om-btn--secondary om-btn--sm';
    full.href = searchUrl(term, this.dataset.searchUrl);
    full.textContent = this.strings.openFull || '';
    actions.append(retry, full);
    box.append(title, text, actions);
    this.message.replaceChildren(box);
    this.message.hidden = false;
    this.say(this.strings.errorTitle || '');
  }

  showIdle() {
    this.setLoading(false);
    this.setActive(-1);
    this.results.hidden = true;
    this.listbox.hidden = true;
    this.listbox.replaceChildren();
    this.message.hidden = true;
    this.message.replaceChildren();
    this.skeleton.hidden = true;
    this.idle.hidden = false;
    this.classList.remove('is-empty');
    this.input.setAttribute('aria-expanded', 'false');
    this.lastTerm = '';
    this.renderRecent();
  }

  setLoading(on) {
    this.classList.toggle('is-loading', on);
    this.results.setAttribute('aria-busy', String(on));
    if (on) {
      this.results.hidden = false;
      // First search: placeholders. Later searches: keep the previous results, slightly dimmed.
      const hasContent = !this.listbox.hidden || !this.message.hidden;
      this.skeleton.hidden = hasContent;
      if (!hasContent) this.idle.hidden = true;
    } else {
      this.skeleton.hidden = true;
    }
  }

  get options() {
    return this.listbox.hidden ? [] : [...this.listbox.querySelectorAll('[role="option"]')];
  }

  setActive(index) {
    const options = this.options;
    options.forEach((o, i) => o.setAttribute('aria-selected', String(i === index)));
    this.activeIndex = index;
    const active = options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  async renderRecent() {
    if (!this.recentBlock) return;
    let list = recent.read();
    // Read and show only with consent to preferences (§ 25 TDDDG); a list left from before a withdrawal is removed.
    if (list.length && !(await preferencesAllowed())) {
      recent.clear();
      list = [];
    }
    this.recentBlock.hidden = list.length === 0;
    this.recentList.replaceChildren(
      ...list.map((term) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'om-chip om-search__chip';
        a.href = searchUrl(term, this.dataset.searchUrl);
        a.dataset.searchTerm = term;
        a.textContent = term;
        li.append(a);
        return li;
      })
    );
  }

  say(message) {
    if (!this.status) return;
    this.status.textContent = '';
    if (message) setTimeout(() => (this.status.textContent = message), 60);
  }
}

/* -------------------------------------------------------- results page */
class OmSearchResults extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('[data-facets-form]');
    this.controller = null;
    this.applyDebounced = debounce(() => this.apply(this.formUrl()), 450);

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('input', (e) => {
      if (e.target.matches?.('[data-price-input]')) this.applyDebounced();
    });
    this.addEventListener('submit', (e) => {
      if (e.target !== this.form) return;
      e.preventDefault();
      this.apply(this.formUrl());
      closeSheet(this.querySelector('dialog[open]'));
    });
    this.addEventListener('click', (e) => this.onClick(e));

    this.onPop = () => this.apply(location.href, { push: false });
    addEventListener('popstate', this.onPop);
  }

  disconnectedCallback() {
    removeEventListener('popstate', this.onPop);
  }

  onChange(event) {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    if (el.matches('[data-price-input]')) return; // handled by the debounced input listener
    if (this.form && el.form === this.form) {
      this.apply(this.formUrl());
      return;
    }
    // Sorting without a filter form (no filters for this query): change only sort_by.
    if (el.matches('select[name="sort_by"]')) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', el.value);
      this.apply(url.href);
    }
  }

  onClick(event) {
    const link = event.target.closest('a[data-facet-link]');
    if (!link) return;
    event.preventDefault();
    this.apply(link.href);
  }

  formUrl() {
    const data = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [key, value] of data) {
      if (typeof value === 'string' && value.trim() === '') continue;
      params.append(key, value);
    }
    for (const [k, v] of Object.entries(AVAILABILITY)) if (!params.has(k)) params.set(k, v);
    return `${location.pathname}?${params.toString()}`;
  }

  async apply(href, { push = true } = {}) {
    const url = new URL(href, location.origin);
    for (const [k, v] of Object.entries(AVAILABILITY)) if (!url.searchParams.has(k)) url.searchParams.set(k, v);
    // New filters or sorting start at page 1; history navigation restores the exact URL.
    if (push) url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setBusy(true);

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    try {
      const response = await fetch(fetchUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swap(doc);
      if (push) history.pushState({ omSearch: true }, '', url.pathname + url.search);
      const count = this.querySelector('[data-results-count]')?.textContent?.trim() || '';
      // While the filter sheet is open the page behind it is inert, so speak from inside the sheet.
      const sheetStatus = this.querySelector('dialog[open] [data-filter-status]');
      if (sheetStatus) {
        sheetStatus.textContent = '';
        setTimeout(() => (sheetStatus.textContent = count), 60);
      } else {
        announce(count);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to a normal page load; the URL carries the full state.
      location.assign(url.pathname + url.search);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setBusy(false);
      }
    }
  }

  swap(doc) {
    const activeId = document.activeElement?.id;
    // Regions outside the filter sheet are replaced whole.
    this.querySelectorAll('[data-swap]').forEach((target) => {
      const fresh = doc.querySelector(`[data-swap="${target.dataset.swap}"]`);
      if (fresh) target.replaceChildren(...fresh.childNodes);
      else target.replaceChildren();
    });
    // Inside the sheet only the value lists change, so open groups and typed prices stay put.
    this.querySelectorAll('[data-facet-values]').forEach((target) => {
      const fresh = doc.querySelector(`[data-facet-values="${target.dataset.facetValues}"]`);
      if (fresh) target.replaceChildren(...fresh.childNodes);
    });
    // Links rendered for the section request carry section_id; strip it so they work as pages.
    this.querySelectorAll('a[href*="section_id="]').forEach((a) => {
      const u = new URL(a.href, location.origin);
      u.searchParams.delete('section_id');
      a.href = u.pathname + u.search;
    });
    if (activeId && document.activeElement?.id !== activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
    // Swapped product cards carry data-reveal; without this they would stay invisible.
    const results = this.querySelector('[data-swap="results"]');
    if (results) initReveal(results);
  }

  setBusy(on) {
    this.toggleAttribute('data-busy', on);
    this.querySelector('[data-swap="results"]')?.setAttribute('aria-busy', String(on));
  }
}

define('om-predictive-search', OmPredictiveSearch);
define('om-search-results', OmSearchResults);

// Theme editor: show the search overlay while its section is selected.
document.addEventListener('shopify:section:select', (event) => {
  if (event.target.querySelector?.('#om-search')) openSheet('om-search');
});
document.addEventListener('shopify:section:deselect', (event) => {
  if (event.target.querySelector?.('#om-search')) closeSheet('om-search');
});
